import { useStoreState } from '@src/model/hooks';
import { usePagination } from '@src/store/hooks/usePagination';
import { useMyReactions } from '@src/store/reactions/hooks/useMyReactions';
import { use, useEffect, useMemo } from 'react';
import {
    type LoadMorePostsParams,
    type PostCollectionCompositeKeyParams,
    type PostCollectionType,
    loadMorePostsAtom,
    postCollectionAtomFamily,
    resetPostCollection,
} from '../posts';

export interface UsePaginatedPostsPayload {
    tokenName: string;
    fetchLimit?: number;
    type: PostCollectionType;
    flairId?: string;
    userId?: string;
    includeMyReactions?: boolean;
    resetPostCollectionOnUnmount?: boolean;
}

export const usePaginatedPosts = (payload: UsePaginatedPostsPayload) => {
    const { includeMyReactions = false, tokenName, type, flairId, userId, resetPostCollectionOnUnmount } = payload;
    const entityType = 'Post';

    const getParams = useMemo(
        () =>
            (payload: UsePaginatedPostsPayload): PostCollectionCompositeKeyParams & { fetchLimit: number } => ({
                type: payload.type,
                tokenName: payload.tokenName,
                fetchLimit: payload.fetchLimit ?? 20,
                ...(payload.flairId && { flairId: payload.flairId }),
                ...(payload.userId && { userId: payload.userId }),
            }),
        [],
    );

    const getLoadMoreParams = useMemo(
        () =>
            (params: PostCollectionCompositeKeyParams & { fetchLimit?: number }, fetchLimit: number): LoadMorePostsParams => ({
                type: params.type,
                tokenName: params.tokenName,
                fetchLimit,
                ...(params.flairId && { flairId: params.flairId }),
                ...(params.userId && { userId: params.userId }),
            }),
        [],
    );

    const { ids, loadMore, hasMore, loadStatus, error } = usePagination(payload, {
        collectionStateFamily: postCollectionAtomFamily,
        loadMoreAtom: loadMorePostsAtom,
        getParams,
        getLoadMoreParams,
        entityType,
        includeMyReactions: includeMyReactions,
    });

    const myUserId = useStoreState(state => state.user.myUserId);
    const idsHash = useMemo(() => ids.join('-'), [ids]);
    const shouldLoadReactions = entityType && includeMyReactions && myUserId;
    const { loadMore: loadMoreReactions } = useMyReactions({
        entityType: entityType,
        entityIds: ids,
    });

    useEffect(() => {
        if (!shouldLoadReactions || !idsHash) return;
        loadMoreReactions();
    }, [idsHash, shouldLoadReactions]);

    // Reset on unmount
    useEffect(() => {
        return () => {
            if (!resetPostCollectionOnUnmount) return;
            resetPostCollection({ type, tokenName, userId });
        };
    }, [resetPostCollectionOnUnmount, type, tokenName, userId]);

    return {
        postIds: ids,
        loadMore,
        hasMore,
        loadStatus,
        error,
    };
};
